import { type Channel, type ProgramGuideItem, type OnDemandItem, TvQualifier, PlayItem } from "@ca.vmedia.tv/domains";
import { useGetDescription } from "@/composables/useGetDescription";
import { Strings } from "@/locales/types/enums/strings";
import { TimeUtils} from '@/utils';
import type { IProgramInfo } from "./types";

export const useProgramInfo = (
        item: ProgramGuideItem | OnDemandItem | PlayItem | null,
        channel: Channel | null,
        srcImg: string | null,
): IProgramInfo => {
    const { $i18n } = useNuxtApp();
    const {isTimeFormat12} = useAppConfig();

    let descriptiveAudio: boolean = false;
    let closedCaptions: boolean = false;
    if (item !== null && 'Qualifiers' in item && item.Qualifiers) {
        descriptiveAudio = item.Qualifiers.includes(TvQualifier.Dvs) && !!channel?.isDescriptiveAudio;
        closedCaptions = item.Qualifiers.includes(TvQualifier.Cc);
    }
    
    const { description } = useGetDescription(item);
    const _advisoryText: string | null = typeof item?.advisory === 'number' ? $i18n.t(`${Strings.ADVISORY_TEXT}.${item.advisory}`).toString() : null;
    const _convertTimeForProgram = (timeMs: number): string => {
        const timeFormat = isTimeFormat12 ? 'hh:mm a' : 'HH:mm';
        return TimeUtils.getMoment($i18n.locale, timeMs).format(timeFormat).toUpperCase(); 
    }
    const _durationText = (lengthInS: number | null): string | null => {
        if (lengthInS){
            const labelHour: string = $i18n.t(Strings.TIME_SHORTHOUR).toString();
            const labelMinute: string = $i18n.t(Strings.TIME_SHORTMINUTE).toString();
            const  hour = Math.trunc(lengthInS / 3600);
            const [minute] = TimeUtils.getMoment($i18n.locale, lengthInS * 1000).format('mm').split(':');
            const duration: string[] = [];
            
            if (hour !== 0){
                duration.push(`${hour}${labelHour}`);
            } 
            if (Number(minute) !== 0){
                duration.push(`${Number(minute)}${labelMinute}`);
            } 

            if (duration.length === 3) {
                duration.pop();
            }

            return duration.join(' ');
        } else {
            return null;
        }
    };

    const _time = (): string | null => {
        if(item?.recentAirTime && item.recentAirTime.startTime) {
            return `${_convertTimeForProgram(item.recentAirTime.startTime)} - ${_convertTimeForProgram(item.recentAirTime.startTime + Number(item.lengthInS) * 1000)}`;
        } else {
            return _durationText(item?.lengthInS ?? null);
        }
    };


    return {
        programName: item?.name ?? null,
        channelCallSign: channel ? channel.callSign : null,
        channelNumber: channel ? channel.accessNum : null,
        time: _time(),
        description: description.value,
        srcImg: srcImg,
        advisoryText: _advisoryText,
        advisory: item?.advisory ?? null,
        highDefinition: false,
        closedCaptions: closedCaptions,
        descriptiveAudio: descriptiveAudio,
        isRestartAvailable: PlayItem.isRestartAvailable(item, channel)
    }
};
